import type { ReactNode } from 'react';
import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { noop } from 'lodash';

import { useClaim } from '~/components/ClaimContainer';
import { WriteFreezeProvider } from '~/components/core/Permissions/WriteFreeze';

import { openSorClaimPage } from './sorClaimWrites';
import { SorWaitingBanner } from './SorWaitingBanner';
import TEXTS from './texts';
import type { SorClaimLock } from './useSorClaimLock';
import { useSorClaimLock } from './useSorClaimLock';

const SorClaimLockContext = React.createContext<SorClaimLock>({
  attempt: null,
  sorName: TEXTS.defaultSorName,
  isSorEnabled: false,
  reload: noop,
});

/**
 * The claim lock of the claim page: while the organization's System of Record has yet to confirm a change on the
 * claim, every write on it is frozen, and once the change settles the claim is read again, so its exposures and
 * actions show what the SoR made of it. For an organization with the SoR flag, it also opens the page to the SoR
 * interceptor, and shows how long a claim write has waited on the SoR.
 */
export const SorClaimLockProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { claim, onClaimUpdate } = useClaim({ claimRequired: true });
  const { attempt, sorName, isSorEnabled, reload } = useSorClaimLock(claim.id);

  const hadOpenAttemptRef = useRef(false);
  useEffect(() => {
    if (hadOpenAttemptRef.current && !attempt) {
      onClaimUpdate?.();
    }
    hadOpenAttemptRef.current = Boolean(attempt);
  }, [attempt, onClaimUpdate]);

  useEffect(() => (isSorEnabled ? openSorClaimPage({ sorName }) : undefined), [isSorEnabled, sorName]);

  const value = useMemo(() => ({ attempt, sorName, isSorEnabled, reload }), [attempt, sorName, isSorEnabled, reload]);

  return (
    <SorClaimLockContext.Provider value={value}>
      <WriteFreezeProvider reason={attempt ? TEXTS.freezeReason(sorName, attempt.label) : null}>
        {children}
      </WriteFreezeProvider>
      <SorWaitingBanner sorName={sorName} />
    </SorClaimLockContext.Provider>
  );
};

export const useSorClaimLockContext = (): SorClaimLock => useContext(SorClaimLockContext);
